import type { FiscalDocumentState } from '@optefiscal/fiscal-domain';
import { presentFiscalState, type StateShape } from '../lib/fiscal-state.js';

/**
 * Ícones outline, 1.5–2 px, visual técnico (Manual, seção 08).
 * Cada estado tem uma FORMA distinta, para que a informação sobreviva sem cor.
 */
function StateIcon({ shape }: { shape: StateShape }) {
  const common = {
    width: 14,
    height: 14,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className: 'of-state__icon',
    'aria-hidden': true,
  };

  switch (shape) {
    case 'check':
      return (
        <svg {...common}>
          <path d="M20 6 9 17l-5-5" />
        </svg>
      );
    case 'clock':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      );
    case 'alert':
      return (
        <svg {...common}>
          <path d="M12 3 2 20h20L12 3z" />
          <path d="M12 10v4" />
          <path d="M12 17h.01" />
        </svg>
      );
    case 'cross':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="m15 9-6 6M9 9l6 6" />
        </svg>
      );
    case 'question':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M9.2 9a2.8 2.8 0 1 1 3.6 2.7c-.5.2-.8.7-.8 1.3v.5" />
          <path d="M12 17h.01" />
        </svg>
      );
    case 'archive':
      return (
        <svg {...common}>
          <rect x="3" y="4" width="18" height="5" rx="1.5" />
          <path d="M5 9v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9" />
          <path d="M10 13h4" />
        </svg>
      );
    default:
      return null;
  }
}

interface Props {
  state: FiscalDocumentState;
  /** Código oficial do autorizador (protocolo, código de rejeição). */
  code?: string;
}

/**
 * Indicador de estado fiscal.
 *
 * Sempre entrega os três sinais exigidos pelo Manual (seção 10):
 * TEXTO (rótulo) + ÍCONE (forma) + CÓDIGO (quando houver).
 * A cor pinta apenas a borda e o ícone — nunca é o único portador da
 * informação, e o rótulo usa cor de texto aprovada em contraste AA.
 */
export function FiscalStateBadge({ state, code }: Props) {
  const presentation = presentFiscalState(state);
  return (
    <span
      className={`of-state of-state--${presentation.tone}`}
      title={presentation.meaning}
      style={{ color: 'var(--of-text-primary)' }}
    >
      <span style={{ color: `var(--of-state-${presentation.tone})`, display: 'inline-flex' }}>
        <StateIcon shape={presentation.shape} />
      </span>
      <span>{presentation.label}</span>
      {code ? <span className="of-state__code">{code}</span> : null}
    </span>
  );
}
